import { useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Panel } from '../components/Panel'
import { Button } from '../components/Button'
import { StatusDot } from '../components/StatusDot'
import { api, type CampaignResults } from '../api/client'
import { fmtInt, fmtRelative } from '../lib/format'

// Canonical status order: funnel left to right (delivered → opened → clicked),
// then transient states (accepted/queued), then terminal failure. Matches the
// mental model of "how far through the funnel did each message get."
const STATUS_ORDER = [
  'delivered', 'opened', 'clicked', 'accepted', 'queued',
  'skipped_variable_null', 'skipped_undeliverable', 'failed',
] as const

// Funnel stages that chain (delivered < opened < clicked). A message in a
// later bucket also counts toward earlier ones. `accepted` / `queued` /
// `failed` are terminal / transient and shown as their raw bucket count.
const FUNNEL_FORWARD: Record<string, readonly string[]> = {
  delivered: ['delivered', 'opened', 'clicked'],
  opened: ['opened', 'clicked'],
  clicked: ['clicked'],
}

function countAtOrBeyond(
  key: string,
  breakdown: Record<string, number>,
): number {
  const stages = FUNNEL_FORWARD[key]
  if (!stages) return breakdown[key] ?? 0
  return stages.reduce((sum, k) => sum + (breakdown[k] ?? 0), 0)
}
const STATUS_TONE: Record<string, string> = {
  clicked: 'text-radar-cyan',
  opened: 'text-radar-green',
  delivered: 'text-paper',
  accepted: 'text-paper-dim',
  queued: 'text-paper-trace',
  skipped_variable_null: 'text-amber-glow',
  skipped_undeliverable: 'text-amber-glow',
  failed: 'text-radar-red',
}

export function CampaignResultsPage() {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const [windowDays, setWindowDays] = useState<number | ''>('')
  const effectiveWindow = typeof windowDays === 'number' ? windowDays : undefined
  const [statusFilter, setStatusFilter] = useState<string | null>(null)

  const results = useQuery({
    queryKey: ['campaign-results', id, effectiveWindow],
    queryFn: () =>
      api.get<CampaignResults>(
        `/api/campaigns/${id}/results` +
        (effectiveWindow !== undefined ? `?window_days=${effectiveWindow}` : ''),
      ),
    refetchInterval: 10_000,
  })

  const refresh = useMutation({
    mutationFn: () => api.post<{ status: string; applied: number; skipped: number }>(
      `/api/campaigns/${id}/results/refresh`,
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-results', id] })
    },
  })

  const saveWindow = useMutation({
    mutationFn: (days: number) => api.put<{ id: number }>(
      `/api/campaigns/${id}/attribution-window`,
      { attribution_window_days: days },
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-results', id] })
      queryClient.invalidateQueries({ queryKey: ['campaign', id] })
    },
  })

  const data = results.data
  const c = data?.campaign

  const orderedStatuses = useMemo(() => {
    if (!data) return []
    const keys = Object.keys(data.status_breakdown)
    const canonical = STATUS_ORDER.filter((k) => keys.includes(k))
    const extras = keys.filter((k) => !(STATUS_ORDER as readonly string[]).includes(k))
    return [...canonical, ...extras]
  }, [data])

  // Sortable recipients table — default is 'status' ascending so the worst
  // outcomes (failed / skipped_undeliverable) show first, matching what an
  // operator usually wants to triage.
  type SortKey = 'email' | 'status' | 'clicks' | 'last_event' | 'renewed' | 'detail'
  const [sortKey, setSortKey] = useState<SortKey>('status')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDir('desc')
    }
  }

  const filteredRecipients = useMemo(() => {
    if (!data) return []
    let rows = data.recipients
    if (statusFilter) {
      // Funnel stages filter cumulatively (clicking "opened" shows openers +
      // clickers); other buckets filter by exact status.
      const stages = FUNNEL_FORWARD[statusFilter]
      if (stages) {
        const set = new Set(stages)
        rows = rows.filter((r) => set.has(r.status))
      } else {
        rows = rows.filter((r) => r.status === statusFilter)
      }
    }
    // Rank used for status sort. Worst-outcome first so failed / skipped
    // rise to the top when sort direction is 'desc' — that's the operator
    // triage default.
    const STATUS_RANK: Record<string, number> = {
      skipped_variable_null: 8, skipped_undeliverable: 7,
      failed: 6, clicked: 5, opened: 4,
      delivered: 3, accepted: 2, queued: 1,
    }
    const cmp = (a: typeof rows[number], b: typeof rows[number]) => {
      let av: string | number = '', bv: string | number = ''
      switch (sortKey) {
        case 'email':
          av = (a.to_email || '').toLowerCase()
          bv = (b.to_email || '').toLowerCase()
          break
        case 'status':
          av = STATUS_RANK[a.status] ?? 0
          bv = STATUS_RANK[b.status] ?? 0
          break
        case 'clicks':
          av = a.click_count
          bv = b.click_count
          break
        case 'last_event':
          av = a.last_event?.at ?? ''
          bv = b.last_event?.at ?? ''
          break
        case 'renewed':
          // Renewed rows sort above non-renewed; within renewed, by date.
          av = a.renewed_at ?? ''
          bv = b.renewed_at ?? ''
          break
        case 'detail':
          av = (a.failure_reason || '').toLowerCase()
          bv = (b.failure_reason || '').toLowerCase()
          break
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1
      if (av > bv) return sortDir === 'asc' ? 1 : -1
      return 0
    }
    // Work on a copy — React state arrays shouldn't be mutated in place.
    return [...rows].sort(cmp)
  }, [data, statusFilter, sortKey, sortDir])

  // Industry-standard email rates. `status_breakdown` buckets are mutually
  // exclusive (each send sits in one bucket representing the furthest state
  // it reached), so we must SUM the funnel cumulatively to get "total
  // delivered" — a message that was opened was also delivered.
  //   Delivery Rate = (delivered + opened + clicked) / sent
  //   Open Rate     = unique_openers / total_delivered
  //   Click Rate    = unique_clickers / total_delivered  (CTR; most common)
  //   CTOR          = unique_clickers / unique_openers
  //   Bounce Rate   = failed / sent
  const totalDelivered = data
    ? (data.status_breakdown.delivered ?? 0)
      + (data.status_breakdown.opened ?? 0)
      + (data.status_breakdown.clicked ?? 0)
    : 0
  const deliveryRate = data && data.sends_total > 0
    ? Math.round((totalDelivered / data.sends_total) * 100)
    : null
  const openRate = totalDelivered > 0
    ? Math.round((data!.engagement.unique_openers / totalDelivered) * 100)
    : null
  const clickRate = totalDelivered > 0
    ? Math.round((data!.engagement.unique_clickers / totalDelivered) * 100)
    : null
  const ctor = data && data.engagement.unique_openers > 0
    ? Math.round((data.engagement.unique_clickers / data.engagement.unique_openers) * 100)
    : null
  const bounceRate = data && data.sends_total > 0
    ? Math.round(((data.status_breakdown.failed ?? 0) / data.sends_total) * 100)
    : null

  return (
    <div className="p-8 space-y-6 max-w-[1400px]">
      <div className="flex items-center gap-3">
        <Link to={`/campaigns/${id}`} className="label-amber hover:underline">← Campaign</Link>
        <span className="label text-paper-trace">/</span>
        <span className="label">Results</span>
      </div>

      <header className="flex items-end justify-between border-b border-ink-500 pb-6">
        <div>
          <div className="label mb-1">Campaign results · live</div>
          <h1 className="font-serif text-[36px] leading-[1.05] tracking-tight text-paper">
            {c?.name ?? 'Loading…'}
          </h1>
          {c && (
            <>
              <div className="flex items-center gap-2 mt-2 font-mono text-[12px] text-paper-dim">
                <StatusDot tone={
                  c.status === 'running' ? 'warn'
                  : c.status === 'scheduled' ? 'active'
                  : c.status === 'completed' ? 'ok'
                  : c.status === 'failed' ? 'err'
                  : 'muted'
                } />
                <span>{c.status}</span>
                {c.last_fired_at && (
                  <>
                    <span className="text-paper-trace">·</span>
                    <span>fired {fmtRelative(c.last_fired_at)}</span>
                  </>
                )}
                <span className="text-paper-trace">·</span>
                <span>{c.from_email ?? '—'}</span>
                <span className="text-paper-trace">·</span>
                <span className="label text-paper-faint">
                  {c.mailgun_tags.length > 0 ? 'tags' : 'tag'}
                </span>
                <code className="text-amber-glow select-all">campaign-{c.id}</code>
                {c.mailgun_tags.map((t) => (
                  <code key={t} className="text-paper-dim select-all">{t}</code>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="flex items-center gap-3">
          {results.isFetching && (
            <span className="label text-paper-trace animate-pulse">REFRESHING…</span>
          )}
          <Button
            variant="outline"
            size="md"
            disabled={refresh.isPending}
            onClick={() => refresh.mutate()}
          >
            {refresh.isPending ? 'Pulling…' : 'Pull engagement from Mailgun'}
          </Button>
        </div>
      </header>

      {refresh.isSuccess && (
        <div className="font-mono text-[12px] text-radar-green">
          ✓ pulled {refresh.data?.applied} event(s) from Mailgun
          {(refresh.data?.skipped ?? 0) > 0 && ` (${refresh.data?.skipped} other-tag events skipped)`}
        </div>
      )}
      {refresh.error instanceof Error && (
        <div className="font-mono text-[12px] text-radar-red">
          Refresh error: {refresh.error.message}
        </div>
      )}

      {!data ? (
        <div className="label text-paper-trace p-12 text-center">LOADING…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat
              label="Sends"
              value={fmtInt(data.sends_total)}
              sub={totalDelivered > 0 ? `${fmtInt(totalDelivered)} delivered` : undefined}
            />
            <Stat
              label="Delivery"
              value={deliveryRate !== null ? `${deliveryRate}%` : '—'}
              tone="text-radar-green"
              sub={bounceRate !== null ? `${bounceRate}% bounced` : undefined}
            />
            <Stat
              label="Open rate"
              value={openRate !== null ? `${openRate}%` : '—'}
              tone="text-radar-cyan"
              sub={data.engagement.unique_openers > 0 ? `${fmtInt(data.engagement.unique_openers)} unique` : undefined}
            />
            <Stat
              label="Click rate"
              value={clickRate !== null ? `${clickRate}%` : '—'}
              tone="text-radar-cyan"
              sub={ctor !== null ? `CTOR ${ctor}%` : undefined}
            />
            <Stat
              label="Conversions"
              value={fmtInt(data.conversions.cohort_count)}
              tone="text-amber-glow"
              sub={data.conversions.cohort_count > 0 && totalDelivered > 0
                ? `${Math.round((data.conversions.cohort_count / totalDelivered) * 100)}% of delivered`
                : undefined}
            />
          </div>

          {data.waves && data.waves.length > 1 && (
            <Panel label="Wave breakdown" accent="amber">
              <div className="overflow-x-auto">
                <table className="w-full font-mono text-[12px] tabular-nums">
                  <thead>
                    <tr className="border-b border-ink-500 text-paper-dim">
                      <th className="text-left py-1.5 pr-3 font-normal label">Wave</th>
                      <th className="text-left py-1.5 pr-3 font-normal label">Sent at</th>
                      <th className="text-right py-1.5 pr-3 font-normal label">Sent</th>
                      <th className="text-right py-1.5 pr-3 font-normal label">Delivered</th>
                      <th className="text-right py-1.5 pr-3 font-normal label">Opened</th>
                      <th className="text-right py-1.5 font-normal label">Clicked</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.waves.map((w) => {
                      const fired = w.fired_at
                        ? new Date(w.fired_at).toLocaleString()
                        : null
                      const scheduled = w.scheduled_at
                        ? new Date(w.scheduled_at).toLocaleString()
                        : null
                      const showStatus = !fired
                      return (
                        <tr key={w.day_offset === null ? 'initial' : w.day_offset} className="border-b border-ink-700/40">
                          <td className="py-1.5 pr-3 text-paper">{w.label}</td>
                          <td className="py-1.5 pr-3 text-paper-dim">
                            {fired ?? (scheduled ? <em>scheduled {scheduled}</em> : '—')}
                          </td>
                          <td className="py-1.5 pr-3 text-right text-paper">
                            {showStatus ? '—' : fmtInt(w.sent_count)}
                          </td>
                          <td className="py-1.5 pr-3 text-right text-paper">
                            {showStatus ? '—' : fmtInt(w.delivered)}
                          </td>
                          <td className="py-1.5 pr-3 text-right text-radar-green">
                            {showStatus ? '—' : fmtInt(w.opened)}
                          </td>
                          <td className="py-1.5 text-right text-radar-cyan">
                            {showStatus ? '—' : fmtInt(w.clicked)}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              <div className="mt-3 font-mono text-[10px] text-paper-trace leading-relaxed">
                Each wave re-sends to the original Day-1 recipients who have
                not clicked any prior send for this campaign. Suppressed and
                undeliverable subscribers are skipped automatically.
              </div>
            </Panel>
          )}

          <Panel label="Mailgun status" accent="cyan">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
              {orderedStatuses.map((key) => {
                // Cumulative funnel counts: each tile shows how many messages
                // reached THIS stage OR beyond. A message that was clicked
                // was also opened and delivered, so it contributes to all
                // three tiles. Matches industry-standard funnel displays
                // (Mailchimp / SendGrid / Postmark) and makes the numbers
                // match the hero row's unique_openers / unique_clickers.
                const count = countAtOrBeyond(key, data.status_breakdown)
                const active = statusFilter === key
                const pct = data.sends_total > 0
                  ? (count / data.sends_total) * 100
                  : 0
                return (
                  <button
                    key={key}
                    onClick={() => setStatusFilter(active ? null : key)}
                    className={`
                      text-left border px-3 py-2 transition-colors
                      ${active
                        ? 'border-amber-glow bg-ink-700/60'
                        : 'border-ink-500 hover:bg-ink-700/30'}
                    `}
                  >
                    <div className={`label ${STATUS_TONE[key] ?? 'text-paper-dim'}`}>
                      {key}
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="font-mono text-[22px] tabular-nums text-paper">
                        {fmtInt(count)}
                      </span>
                      <span className="font-mono text-[11px] tabular-nums text-paper-trace">
                        {pct >= 10 ? pct.toFixed(0) : pct.toFixed(1)}%
                      </span>
                    </div>
                  </button>
                )
              })}
            </div>
            <div className="mt-3 font-mono text-[11px] text-paper-trace grid grid-cols-3 gap-4">
              <div>Unique openers: <span className="text-paper">{fmtInt(data.engagement.unique_openers)}</span></div>
              <div>Unique clickers: <span className="text-paper">{fmtInt(data.engagement.unique_clickers)}</span></div>
              <div>Total clicks: <span className="text-paper">{fmtInt(data.engagement.total_clicks)}</span></div>
            </div>
          </Panel>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Panel label="Attributed conversions" accent="amber">
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-4 pb-3 border-b border-ink-500">
                  <div>
                    <div className="label text-paper-faint">Paid orders in window</div>
                    <div className="font-mono text-[28px] leading-tight text-amber-glow tabular-nums">
                      {fmtInt(data.conversions.cohort_count)}
                    </div>
                  </div>
                  <div>
                    <div className="label text-paper-faint">Click-through</div>
                    <div className="font-mono text-[28px] leading-tight text-radar-cyan tabular-nums">
                      {fmtInt(data.conversions.clickthrough_count)}
                    </div>
                  </div>
                  <div>
                    <div className="label text-paper-faint">Revenue</div>
                    <div className="font-mono text-[28px] leading-tight text-radar-green tabular-nums">
                      {data.conversions.cohort_revenue > 0
                        ? `$${data.conversions.cohort_revenue.toFixed(2)}`
                        : '—'}
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="label text-paper-faint">Attribution window</div>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      min={1}
                      max={365}
                      placeholder={String(c?.attribution_window_days ?? 7)}
                      value={windowDays}
                      onChange={(e) => {
                        const v = e.target.value
                        setWindowDays(v === '' ? '' : Number(v))
                      }}
                      className="w-20 bg-transparent border-b border-ink-500 font-mono text-[14px] text-paper py-1 focus:outline-none focus:border-amber-glow"
                    />
                    <span className="label text-paper-dim">days</span>
                    <span className="label text-paper-trace">
                      (campaign default: {c?.attribution_window_days ?? 7}d)
                    </span>
                    {typeof windowDays === 'number' && windowDays !== c?.attribution_window_days && (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={saveWindow.isPending}
                        onClick={() => saveWindow.mutate(windowDays)}
                      >
                        {saveWindow.isPending ? 'Saving…' : 'Save as default'}
                      </Button>
                    )}
                  </div>
                  <div className="font-mono text-[10px] text-paper-trace">
                    Counts paid orders (renewals and rejoins) by any recipient whose order_date falls within
                    the window after this campaign's fire time. Click-through
                    requires a Mailgun `clicked` event to precede the order.
                  </div>
                </div>

                {data.conversions.orders.length > 0 && (
                  <div className="border-t border-ink-500 pt-3">
                    <div className="label text-paper-faint mb-2">
                      Attributed orders ({data.conversions.orders.length})
                    </div>
                    <div className="max-h-[220px] overflow-auto border border-ink-500">
                      <table className="w-full text-[11px] font-mono">
                        <thead className="sticky top-0 bg-ink-800 border-b border-ink-500">
                          <tr className="text-paper-faint">
                            <th className="px-2 py-1 text-left">Date</th>
                            <th className="px-2 py-1 text-left">Order ID</th>
                            <th className="px-2 py-1 text-left">Sub #</th>
                            <th className="px-2 py-1 text-right">Amount</th>
                            <th className="px-2 py-1 text-left">Type</th>
                            <th className="px-2 py-1 text-left">Click?</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.conversions.orders.map((o) => (
                            <tr key={o.simplecirc_order_id} className="border-b border-ink-500/40">
                              <td className="px-2 py-1 text-paper-dim">
                                {o.order_date ? fmtRelative(o.order_date) : '—'}
                              </td>
                              <td className="px-2 py-1 text-paper">{o.simplecirc_order_id}</td>
                              <td className="px-2 py-1 text-paper-dim">
                                <Link to={`/subscribers/${o.subscriber_id}`} className="hover:text-amber-glow hover:underline">
                                  {o.subscriber_id}
                                </Link>
                              </td>
                              <td className="px-2 py-1 text-right text-paper tabular-nums">
                                {o.amount_paid !== null ? `$${o.amount_paid.toFixed(2)}` : '—'}
                              </td>
                              <td className="px-2 py-1 text-paper-dim">{o.new_or_renewal ?? '—'}</td>
                              <td className="px-2 py-1">
                                {o.had_click ? (
                                  <span className="text-radar-cyan">✓</span>
                                ) : (
                                  <span className="text-paper-trace">—</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </Panel>

            <Panel label="Top clicked links" accent="cyan">
              {data.top_links.length === 0 ? (
                <div className="label text-paper-trace p-6 text-center">
                  No clicks recorded yet.
                </div>
              ) : (
                <div className="max-h-[360px] overflow-auto">
                  <table className="w-full text-[11px] font-mono">
                    <thead className="sticky top-0 bg-ink-800 border-b border-ink-500">
                      <tr className="text-paper-faint">
                        <th className="px-2 py-1 text-left">URL</th>
                        <th className="px-2 py-1 text-right w-[90px]">Clicks</th>
                        <th className="px-2 py-1 text-right w-[90px]">Unique</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.top_links.map((l) => (
                        <tr key={l.url} className="border-b border-ink-500/40">
                          <td className="px-2 py-1 text-paper-dim truncate max-w-[460px]">
                            <a
                              href={l.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:text-amber-glow"
                              title={l.url}
                            >
                              {l.url}
                            </a>
                          </td>
                          <td className="px-2 py-1 text-right text-paper tabular-nums">
                            {fmtInt(l.clicks)}
                          </td>
                          <td className="px-2 py-1 text-right text-paper-dim tabular-nums">
                            {fmtInt(l.unique_clickers)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>
          </div>

          <Panel
            label="Recipients"
            accent="none"
            right={
              <span className="label text-paper-trace">
                {filteredRecipients.length} shown
                {data.recipients_truncated && ` · ${data.sends_total} total (first 500, plus everyone who paid)`}
                {statusFilter && (
                  <>
                    {' '}<button
                      onClick={() => setStatusFilter(null)}
                      className="text-amber-glow hover:underline ml-1"
                    >
                      clear filter
                    </button>
                  </>
                )}
              </span>
            }
          >
            <div className="max-h-[520px] overflow-auto border border-ink-500">
              <table className="w-full text-[12px] font-mono">
                <thead className="sticky top-0 bg-ink-800 border-b border-ink-500">
                  <tr className="text-paper-faint">
                    <SortHeader label="Email" k="email" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                    <SortHeader label="Status" k="status" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} width="90px" />
                    <SortHeader label="Clicks" k="clicks" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} width="70px" align="right" />
                    <SortHeader label="Last event" k="last_event" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} width="140px" />
                    <SortHeader label="Renewed" k="renewed" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} width="140px" />
                    <SortHeader label="Detail" k="detail" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                  </tr>
                </thead>
                <tbody>
                  {filteredRecipients.map((r) => (
                    <tr key={r.id} className="border-b border-ink-500/40 hover:bg-ink-700/20">
                      <td className="px-3 py-1.5 text-paper truncate max-w-[260px]">
                        {r.subscriber_id ? (
                          <Link to={`/subscribers/${r.subscriber_id}`} className="hover:text-amber-glow">
                            {r.to_email}
                          </Link>
                        ) : r.to_email}
                      </td>
                      <td className={`px-3 py-1.5 ${STATUS_TONE[r.status] ?? 'text-paper-dim'}`}>
                        {r.status}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-paper-dim">
                        {r.click_count > 0 ? (
                          <span className="text-radar-cyan">{r.click_count}</span>
                        ) : '—'}
                      </td>
                      <td className="px-3 py-1.5 text-paper-trace">
                        {r.last_event
                          ? `${r.last_event.event} · ${fmtRelative(r.last_event.at)}`
                          : '—'}
                      </td>
                      <td className="px-3 py-1.5">
                        {r.renewed_at ? (
                          <span className="text-amber-glow">✓ {fmtRelative(r.renewed_at)}</span>
                        ) : (
                          <span className="text-paper-trace">—</span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-paper-dim truncate max-w-[320px]">
                        {r.failure_reason ?? ''}
                      </td>
                    </tr>
                  ))}
                  {filteredRecipients.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-3 py-8 text-center text-paper-trace">
                        No recipients match this filter.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Panel>
        </>
      )}
    </div>
  )
}

function SortHeader<K extends string>({
  label, k, sortKey, sortDir, onClick, width, align,
}: {
  label: string
  k: K
  sortKey: K
  sortDir: 'asc' | 'desc'
  onClick: (k: K) => void
  width?: string
  align?: 'left' | 'right'
}) {
  const active = sortKey === k
  const arrow = active ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''
  const alignClass = align === 'right' ? 'text-right' : 'text-left'
  return (
    <th
      style={width ? { width } : undefined}
      className={`px-3 py-2 ${alignClass} cursor-pointer select-none hover:text-paper whitespace-nowrap`}
      onClick={() => onClick(k)}
    >
      <span className={active ? 'text-amber-glow' : ''}>
        {label}{arrow}
      </span>
    </th>
  )
}


function Stat({
  label, value, tone, sub,
}: { label: string; value: string; tone?: string; sub?: string }) {
  return (
    <div className="border border-ink-500 bg-ink-800/40 p-3">
      <div className="label text-paper-faint">{label}</div>
      <div className={`font-mono text-[26px] tabular-nums ${tone ?? 'text-paper'}`}>{value}</div>
      {sub && (
        <div className="font-mono text-[10px] text-paper-trace mt-0.5 tabular-nums">
          {sub}
        </div>
      )}
    </div>
  )
}
